import get from 'lodash-es/get';
import sortBy from 'lodash-es/sortBy';
import uniq from 'lodash-es/uniq';

import router from '@/router';
import global from '@/store/global';
import {
  AmountT,
  ChfAmountT,
  EokParsedT,
  EpinParsedT,
  EventDescriptionT,
  IbanFormatT,
  SmsParsedT,
  StT,
  mTokenParsedT,
} from '@/support/types';
import { messages } from '@/translations/messages.js';

type VueI18n = typeof messages;
export default class Helpers {
  // translation string
  i18n!: VueI18n;

  constructor(i18n: VueI18n) {
    this.i18n = i18n;
  }

  /**
   * Changes document title
   *
   * @param {string} docTitle
   */
  setDocumentTitle = (docTitle: string): void => {
    document.title = docTitle;
  };

  /**
   * Converts seconds into minute format mm:ss
   *
   * @param {number} seconds
   * @return {string} mm:ss
   */
  static secondsToMinutes(seconds: number): string {
    if (!seconds) return '00:00';

    const m =
      seconds / 60 < 10
        ? `0${Math.floor(seconds / 60)}`
        : `${Math.floor(seconds / 60)}`;
    const s = seconds % 60 < 10 ? `0${seconds % 60}` : `${seconds % 60}`;
    return `${m}:${s}`;
  }

  /**
   * Function creates unique user id UUID v4.
   * Script is RFC4122 compliant.
   *
   * https://tools.ietf.org/html/rfc4122
   *
   * @returns uuid {string}
   */
  static generateUUID(): string {
    return crypto.randomUUID();
  }

  /**
   * Used to grab path name for secv redirect links.
   * Omits / since those would throw error
   *
   * @returns pathValue {string}
   */
  static getPathName(): string {
    // allowed paths for restriction, same as in node
    const knownPaths = [
      'zmena-hesla',
      'sporopay-kluc',
      'bezpecnostne-predmety',
      'tpp',
    ];

    const path = window.location.pathname.slice(1).split('/')[0];
    return knownPaths.includes(path) ? `/${path}` : '';
  }

  /**
   * Returns formated string value
   *
   * @param iban {string}
   */
  static formatIban(iban: string): string {
    // split iban per info category
    const splitedIban: IbanFormatT = {
      countryCode: iban.substr(0, 4),
      bankIdentifier: iban.substr(4, 4),
      branchIdentifier: iban.substr(8, 6),
      accountNumber: iban.substr(14),
    };

    return `<span class="iban">${splitedIban.countryCode} <strong>${splitedIban.bankIdentifier}</strong> ${splitedIban.branchIdentifier} <strong>${splitedIban.accountNumber}</strong></span>`;
  }

  /**
   * Parse state param and return it
   *
   * @param state
   * @return string
   */
  static parseState(state: string | undefined): string[] {
    if (state) {
      return state.split('||');
    }
    return [];
  }

  /**
   * Get or set specific param values in url string
   *
   * @param {string} url
   * @param {string} paramName
   * @param {string | null} newValue
   * @param {boolean} strict - If its silent drop, or throw error
   */
  static getSetUrlParams(
    url: string,
    paramName: string,
    newValue: string | null = null,
    strict = false,
  ): undefined | string {
    const urlPart: string = url.split('?')[0];
    const params: { [key: string]: string } = Helpers.extractParams(
      url.split('?')[1],
    );
    let paramValue = '';

    // check if param exists and set it
    if (!params[paramName]) {
      if (strict) {
        throw new Error(`Param ${paramName} can't be found.`);
      } else {
        return undefined;
      }
    }
    paramValue = params[paramName];

    // are we setting it or reading it?
    if (!newValue) {
      return paramValue;
    }

    params[paramName] = newValue;
    return `${urlPart}?${Helpers.objectToParams(params)}`;
  }

  /**
   * Converts object into url params
   *
   * @param {Object} obj
   */
  static objectToParams(obj: { [keyName: string]: string }) {
    return Object.keys(obj)
      .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(obj[k])}`)
      .join('&');
  }

  /**
   * Convert string params, or get params from url and return them in object
   *
   * @param {boolean} removeParams True if you want to remove params from url
   * @returns {}
   */
  static paramsToObject(removeParams: boolean): {} {
    //  #access_token=AQ7O5PJ33AEP6OSEJC5CFYGJRZPX4W7K&state=0f11628f-4490-4c2f-9014-57a5ecfaacb8&lang=sk
    //  #access_token=AQ7O5PJ33AEP6OSEJC5CFYGJRZPX4W7K
    //  ?state=0f11628f-4490-4c2f-9014-57a5ecfaacb8&lang=sk#access_token=AQ7O5PJ33AEP6OSEJC5CFYGJRZPX4W7K
    //  ?state=0f11628f-4490-4c2f-9014-57a5ecfaacb8&lang=sk

    const urlHash: string = window.location.hash.substring(1); // remove #
    const urlSearch: string = window.location.search.substring(1); // remove ?

    if (removeParams) {
      window.history.pushState('', document.title, window.location.pathname);
    }

    return {
      ...Helpers.extractParams(urlHash),
      ...Helpers.extractParams(urlSearch),
    };
  }

  /**
   * Extract params from key=value&key2=value2 string and return as { key: value } object
   *
   * @param {string} paramsString
   * @returns {object}
   */
  static extractParams(paramsString: string): {} {
    return paramsString !== '' && paramsString !== '/'
      ? JSON.parse(
          `{"${decodeURI(paramsString)
            .replace(/"/g, '\\"')
            .replace(/(&)$/g, '')
            .replace(/((=)((?!&).)*(=))/gi, '=')
            .replace(/&/g, '","')
            .replace(/=/g, '":"')}"}`,
        )
      : {};
  }

  /**
   * Template parser for string. Simple example:
   * parseTemplate('${name} is now master of the ${galaxy}', {
   *     name: 'John',
   *     galaxy: 'Milky Way',
   * });
   *
   * Source: https://gist.github.com/smeijer/6580740a0ff468960a5257108af1384e
   *
   * @param {string} template Provide string to be converted
   * @param {object} map Object with mapped keys and translations
   * @param {strign} fallback Function name
   */
  static parseTemplate(
    template: string,
    map: object,
    fallback: string | undefined,
  ) {
    /**
     * Get function for below parser
     */
    function _get(path: string, obj: object, fb = `$\{${path}}`) {
      return path
        .split('.')
        .reduce((res: any, key: string) => res[key] || fb, obj);
    }

    return template.replace(/\$\{.+?}/g, (match) => {
      const path = match.substr(2, match.length - 3).trim();
      return _get(path, map, fallback);
    });
  }

  /**
   * @description Masks phone number, so only some parts are visible
   *
   * @param phoneNumber
   * @param maskingSign
   * @returns {string}
   */
  static maskPhoneNumber(
    phoneNumber: string,
    maskingSign: string = '*',
  ): string {
    const replacedLetters = phoneNumber.substr(5, 5);
    return phoneNumber.replace(
      replacedLetters,
      maskingSign.repeat(replacedLetters.length),
    );
  }

  /**
   * Remove keys will null values from object
   *
   * @param objWithNulls
   * @returns {object}
   */
  static cleanNulls(objWithNulls: {}): {} {
    const strData: string = JSON.stringify(
      objWithNulls,
      (idx: string, obj: {}): {} => {
        for (const propName in obj) {
          if ((obj as any)[propName] === null) {
            delete (obj as any)[propName];
          }
        }
        return obj;
      },
    );
    return JSON.parse(strData);
  }

  /**
   * Show hide content of provided password field
   *
   * @param e {event}
   */
  showHidePassword(e: Event): void {
    const target = e.currentTarget as HTMLElement;
    const input = target.previousSibling as HTMLElement;

    const showPassAria = this.i18n.global
      .t('interface.showPassword')
      .toString();
    const hidePassAria = this.i18n.global
      .t('interface.hidePassword')
      .toString();

    // switch span class, set aria label and change input type
    if (target.classList.contains('showIcon')) {
      // target.classList.replace('show', 'hide');
      target.classList.remove('showIcon');
      target.classList.add('hideIcon');
      target.setAttribute('aria-label', hidePassAria);
      if (input !== null) input.setAttribute('type', 'text');
      input.focus();
    } else {
      // target.classList.replace('hide', 'show');
      target.classList.remove('hideIcon');
      target.classList.add('showIcon');
      target.setAttribute('aria-label', showPassAria);
      if (input !== null) input.setAttribute('type', 'password');
      input.focus();
    }
  }

  /**
   * convert timestamp string into sk format time
   *
   * @param {string} dateString
   * @returns {string}
   */
  static getSkDateFormat(dateString: string): string {
    const fulldate = new Date(
      dateString !== undefined ? dateString.split('Z')[0] : '',
    );
    const day =
      fulldate.getDate() < 10 ? `0${fulldate.getDate()}` : fulldate.getDate();
    const month =
      fulldate.getMonth() + 1 < 10
        ? `0${fulldate.getMonth() + 1}`
        : fulldate.getMonth() + 1;
    return `${day}.${month}.${fulldate.getFullYear()}`;
  }

  /**
   * Remove all except numbers from string
   *
   * @param {string} val
   * @return {string}
   */
  static numberOnly(val: string) {
    return val.replace(/[^\d.]/g, '');
  }

  /**
   * Format time numbers with leading zero
   *
   * @param time
   */
  static addLeadingZero(time: number): string {
    return time > 9 ? `${time}` : `0${time}`;
  }

  /**
   * Generates single mtoken data
   *
   * @param mtokenData
   */
  generateSingleMtokenData(mtokenData: StT): mTokenParsedT {
    return {
      id: mtokenData.id,
      name: mtokenData.name,
      validFrom: this.generateStValidityFrom(
        mtokenData.validFrom,
        mtokenData.state,
      ),
      validFor: this.generateStValidFor(mtokenData.channelGroups),
      actions: mtokenData.actions,
      channelGroups: mtokenData.channelGroups,
    };
  }

  /**
   * Generates single eok data
   *
   * @param eokData
   */
  generateSingleEokData(eokData: StT): EokParsedT {
    return {
      id: eokData.id,
      state: eokData.state,
      name: eokData.name,
      validFrom: this.generateStValidityFrom(eokData.validFrom, eokData.state),
      validFor: this.generateStValidFor(eokData.channelGroups),
      actions: eokData.actions,
      typeCode: eokData.typeCode,
      typeName: eokData.typeName,
      channelGroups: eokData.channelGroups,
    };
  }

  /**
   * Generates single epin data
   *
   * @param epinData
   */
  generateSingleEpinData(epinData: StT): EpinParsedT {
    return {
      id: epinData.id,
      state: epinData.state,
      name: epinData.name,
      blockedTo: epinData.blockedTo
        ? this.generateStValidityTo(epinData.blockedTo, epinData.state)
        : undefined,
      validFrom: this.generateStValidityFrom(
        epinData.validFrom,
        epinData.state,
      ),
      validFor: this.generateStValidFor(epinData.channelGroups),
      actions: epinData.actions,
      typeCode: epinData.typeCode,
      typeName: epinData.typeName,
      channelGroups: epinData.channelGroups,
    };
  }

  /**
   * Generates single sms data
   *
   * @param smsData
   */
  generateSingleSmsData(smsData: StT): SmsParsedT {
    return {
      id: smsData.id,
      state: smsData.state,
      name: smsData.name,
      validFrom: this.generateStValidityFrom(smsData.validFrom, smsData.state),
      validFor: this.generateStValidFor(smsData.channelGroups),
      actions: smsData.actions,
      typeCode: smsData.typeCode,
      typeName: smsData.typeName,
      channelGroups: smsData.channelGroups,
    };
  }

  /**
   * Generates sentence of st validity from specific date
   *
   * @param _timestamp
   * @param state
   */
  generateStValidityFrom(_timestamp: string, state: string): string {
    const translationPath = `pages.authTools.st.${state}`;
    return `${this.i18n.global.t(translationPath)} ${Helpers.generateTimeHTMLTag(_timestamp)}.`;
  }

  /**
   * Generates sentence of st validity to specific date.
   *
   * @param _timestamp
   * @param state
   */
  generateStValidityTo(_timestamp: string, state: string): string {
    const translationPath = `pages.authTools.st.${state}_TO`;
    return `${this.i18n.global.t(translationPath)} ${Helpers.generateTimeHTMLTag(_timestamp)}.`;
  }

  /**
   * Generates Time HTML tag
   *
   * @param _timestamp
   */
  static generateTimeHTMLTag(_timestamp: string): string {
    const timestamp: string = Helpers.timeCorrector(_timestamp);
    const timestampEffective = timestamp || timestamp;

    const t = new Date(timestampEffective);
    const time = `${Helpers.addLeadingZero(t.getHours())}:${Helpers.addLeadingZero(t.getMinutes())}`;
    const date = `${Helpers.addLeadingZero(t.getDate())}.${Helpers.addLeadingZero(t.getMonth() + 1)}.${Helpers.addLeadingZero(t.getFullYear())}`;

    return `<time datetime="${_timestamp}">${date}, ${time}</time>`;
  }

  /**
   * Generates st groups
   *
   * @param channelsData
   * @param activateForGeorgeFlag
   */
  generateStValidFor(channelsData: string[]): string {
    if (channelsData.length === 0) {
      return '';
    }

    // filler strings
    const activeFor: string = this.i18n.global
      .t('pages.authTools.st.activeFor')
      .toString();

    const lastConnector = this.i18n.global
      .t('pages.authTools.st.activeForAnd')
      .toString();

    // grab active channel names
    const channels: string[] = channelsData.map((channel: string): string => {
      return this.i18n.global.t(`pages.authTools.st.channelGroups.${channel}`);
    });

    // handle connector with comma and a/and
    const channelsLength: number = channels.length;
    let channelsString: string = '';
    if (channelsLength > 1) {
      const lastItem = channels.pop();
      channelsString = `${channels.join(', ')} ${lastConnector} ${lastItem}`;
    } else {
      channelsString = channels.join(', ');
    }

    return `${activeFor} ${channelsString}.`;
  }

  /**
   * Preparation of consent titles
   *
   * @param tppName
   * @param tppConsentGroups
   */
  prepareConsentTitle(tppName: string, tppConsentGroups: string[]) {
    const groups = tppConsentGroups
      .map((group: string) => {
        return this.i18n.global.t(
          `pages.tpp.dashboard.consentScopeGroup.${group}`,
        );
      })
      .join(', ');

    return `${tppName} (${groups.toLowerCase()})`;
  }

  /**
   * Preparation of consent last access
   *
   * @param lastAccess
   */
  prepareConsentLastAccess(lastAccess: string) {
    return `${this.i18n.global.t('pages.tpp.dashboard.lastAccess')} ${Helpers.getSkDateFormat(lastAccess)}`;
  }

  /**
   * Preparation of granted and valid
   *
   * @param grantedAccess
   * @param validTo
   */
  prepareConsentGrantedAndValid(grantedAccess: string, validTo: string) {
    const grantedAccessDate = Helpers.getSkDateFormat(grantedAccess);
    const accessValidTo = Helpers.getSkDateFormat(validTo);

    return this.i18n.global.t('pages.tpp.dashboard.accessGrantedAndValid', {
      dateGranted: grantedAccessDate,
      dateValid: accessValidTo,
    });
  }

  /**
   * Generates random id (or some random string if needed)
   *
   * @returns {string}
   */
  static generateRandomId(): string {
    return `vue_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Converts number to CHF amount type
   *
   * @param val
   * @param precision
   * @param currency
   */
  static toAmountType(
    val: string,
    precision = 2,
    currency = 'EUR',
  ): ChfAmountT | null {
    if (val === undefined || val === '') return null;

    // if its string, make a number from it
    const amount = parseFloat(val);

    return {
      value: amount * 10 ** precision,
      precision,
      currency,
    };
  }

  /**
   * Converts number to CHF amount type
   *
   * @param amount
   */
  static fromAmountType(amount: ChfAmountT): AmountT {
    return {
      value: amount.value / 10 ** amount.precision,
      currency: amount.currency,
    };
  }

  /**
   * Reset given timestamp to midnight
   *
   * @param date
   * @returns number
   */
  static resetToMignight(date: number): number {
    // dateception ... badumtsss
    return (
      new Date(new Date(date).setHours(0, 0, 0, 0)).getTime() -
      new Date(date).getTimezoneOffset() * 60 * 1000
    );
  }

  /**
   * Reset given timestamp to end of day
   *
   * @param date
   * @returns number
   */
  static resetToEndOfTheDay(date: number): number {
    return (
      new Date(new Date(date).setHours(23, 59, 59, 0)).getTime() -
      new Date(date).getTimezoneOffset() * 60 * 1000
    );
  }

  /**
   * Add or remove months from given date
   *
   * @param date
   * @param months
   */
  static addMonths(date: Date, months: number): number {
    date.setMonth(date.getMonth() + months);
    return new Date(date).getTime();
  }

  /**
   * Validate and create timestamp for chf call.
   * Needs to be in this format: YYYY-MM-DDTHH:MM:SS
   * Accepting formats:
   *    RRRR-MM-DD'T'hh:mm:dd (2018-02-13T13:00:00)
   *    RRRR-MM-DD (2018-02-13)
   *    DDMMRRRR (13022018)
   *    DD(./-)MM(./-)YYYY
   *
   * @param timeParamInput
   */
  static parseTimestamp(timeParamInput: string): number {
    let timeParam!: string;

    if (timeParamInput === undefined) timeParam = new Date().toISOString();

    timeParam = timeParamInput;

    // parse date
    const reShort = /(?<year>\d{4})(?<month>\d{2})(?<day>\d{2})/g;
    // eslint-disable-next-line
        const reLong = /(?<day>\d{1,2})[-.\/\s]{1}(?<month>\d{1,2})[-.\/\s]{1}(?<year>\d{4})/g;

    const shortDate = reShort.exec(timeParam);
    const dates: any = shortDate !== null ? shortDate : reLong.exec(timeParam);

    if (
      dates !== null &&
      dates.day !== null &&
      dates.month !== null &&
      dates.year !== null
    ) {
      const regroupedDateString = `${dates.groups.year}-${dates.groups.month}-${dates.groups.day}`;
      return Helpers.parseTimestamp(regroupedDateString);
    }

    return !Number.isNaN(Date.parse(timeParam)) ? Date.parse(timeParam) : NaN;
  }

  /**
   * Returns array of objects grouped by date format of MMYYYY
   * @param data
   * @param sortingKey
   */
  static groupDataByMonthYear(data: any[], sortingKey: string): {}[] {
    // date calculation
    // return string representation of month and year
    const _dateGroupingIndex = (timestamp: string) => {
      const d = new Date(timestamp);
      // eslint-disable-next-line
            const month = (d.getMonth() + 1 < 10) ? `0${d.getMonth() + 1}` : d.getMonth() + 1;
      return `${month}${d.getFullYear()}`;
    };

    // sort timestamps
    const timestamps: number[] = sortBy(
      data.map((group: any): number => {
        return new Date(get(group, sortingKey)).getTime();
      }),
    );

    // generate group keys from sorted timestamps
    const groupKeys: string[] = uniq(
      timestamps.map((timestamp: any): string => {
        return _dateGroupingIndex(timestamp);
      }),
    ).reverse();

    // generate objects for array
    return groupKeys.map((groupKey: string): {} => {
      return {
        groupName: groupKey,
        groupData: data.filter(
          (item) => _dateGroupingIndex(get(item, sortingKey)) === groupKey,
        ),
      };
    });
  }

  /**
   * Returns description of events
   */
  eventsDescription(): EventDescriptionT[] {
    return [
      {
        type: 'consentCreation',
        icon: 'appLogo',
        label: this.i18n.global.t(
          'pages.tpp.history.filter.events.consentCreation',
        ),
      },
      {
        type: 'consentCancellation',
        icon: 'appLogo',
        label: this.i18n.global.t(
          'pages.tpp.history.filter.events.consentCancellation',
        ),
      },
      {
        type: 'accountList',
        icon: 'ico-accounts',
        label: this.i18n.global.t(
          'pages.tpp.history.filter.events.accountList',
        ),
      },
      {
        type: 'paymentInit',
        icon: 'ico-money',
        label: this.i18n.global.t(
          'pages.tpp.history.filter.events.paymentInit',
        ),
      },
      {
        type: 'paymentCancellation',
        icon: 'ico-money',
        label: this.i18n.global.t(
          'pages.tpp.history.filter.events.paymentCancellation',
        ),
      },
      {
        type: 'accountTransactions',
        icon: 'ico-chart-vertical',
        label: this.i18n.global.t(
          'pages.tpp.history.filter.events.accountTransactions',
        ),
      },
      {
        type: 'accountDetail',
        icon: 'ico-accounts',
        label: this.i18n.global.t(
          'pages.tpp.history.filter.events.accountDetail',
        ),
      },
      {
        type: 'accountBalance',
        icon: 'ico-bars-line-chart-outline',
        label: this.i18n.global.t(
          'pages.tpp.history.filter.events.accountBalance',
        ),
      },
      {
        type: 'sufficientFundsCheck',
        icon: 'ico-checkmark-circle',
        label: this.i18n.global.t(
          'pages.tpp.history.filter.events.sufficientFundsCheck',
        ),
      },
    ];
  }

  /**
   * Sort by iso timestamp. Must give path to its value in object
   * Option to asc, desc with bool
   *
   * @param data
   * @param searchString
   * @param asc
   */
  static sortByDate(
    data: any[],
    searchString: string,
    asc: boolean = true,
  ): any[] {
    // create temp key and sort
    let rArr = sortBy(
      data.map((item: any) => {
        item.sortingDate = new Date(get(item, searchString)).getTime();
        return item;
      }),
      'sortingDate',
    );
    // remove temp key
    rArr = rArr.map((item) => {
      delete item.sortingDate;
      return item;
    });
    // return asc or desc
    return asc ? rArr : rArr.reverse();
  }

  /**
   * Format any number
   *
   * @param number
   * @param places
   * @param symbol
   * @param thousand
   * @param decimal
   */
  // eslint-disable-next-line
    static formatNumber(number: any | string, places: number = 2 , symbol: string = ' €', thousand: string = ' ', decimal: string = ',') : string{
    if (!number) return '';

    // negative mark
    // eslint-disable-next-line
        let negative = parseInt(number) < 0 ? '-' : '',
      // some magic ;)
      // eslint-disable-next-line
        i = parseInt(number = Math.abs(+number || 0).toFixed(places), 10) + '',
      // eslint-disable-next-line
        il = i.length,
      // eslint-disable-next-line
        j = il > 3 ? il % 3 : 0;

    // return formated number (another magic ;) )
    // eslint-disable-next-line
        return negative + (j ? i.substr(0, j) + thousand : '') + i.substr(j).replace(/(\d{3})(?=\d)/g, '$1' + thousand) + (places ? decimal + Math.abs(number - (i as any)).toFixed(places).slice(2) : '') + symbol;
  }

  /**
   * Fixes timestamp format for IE
   *
   * @param timestamp - ISO timestamp
   * @returns Adjusted timestamp
   */
  static timeCorrector(timestamp: string): string {
    const pluspattern: any[] | null = timestamp.match(/[+](.*)/);
    let timestampAdjusted: string | null = null;

    if (
      pluspattern !== null &&
      !pluspattern[0].includes(':') &&
      pluspattern[0].length === 5
    ) {
      const correction: string = `${pluspattern[0].slice(0, 3)}:${pluspattern[0].slice(3)}`;
      timestampAdjusted = timestamp.replace(pluspattern[0], correction);
    }

    return timestampAdjusted || timestamp;
  }

  static handlePageLoad(app: string, callback: Function): void {
    if (global.clientInfo?.apps) {
      // check if client have option to be on this page
      // if not, redirect him to main page
      // @ts-ignore
      if (
        Object.hasOwnProperty.call(global.clientInfo.apps, app) &&
        global.clientInfo.apps[app]
      ) {
        callback();
      } else {
        router.push({ name: 'dashboard' });
      }
    }
  }

  static generateCodeVerifier() {
    const array = new Uint8Array(32); // 32 bytes creates a 43-character string
    crypto.getRandomValues(array);

    // Convert to URL-safe Base64 string
    return btoa(String.fromCharCode(...array))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/={1,2}$/, '');
  }

  static async generateCodeChallenge(message: string) {
    const encoder = new TextEncoder();
    const data = encoder.encode(message);
    const hash = await window.crypto.subtle.digest('SHA-256', data);
    const base64 = btoa(String.fromCharCode(...new Uint8Array(hash)));

    const base64UrlSafe = base64
      .replace(/\+/g, '-') // Replace '+' with '-'
      .replace(/\//g, '_') // Replace '/' with '_'
      .replace(/={1,2}$/, ''); // Remove '=' padding

    return base64UrlSafe;
  }
}
